import { useState } from 'react';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');
    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });
      const response = await fetch('https://readdy.ai/api/form/d50r0b2opjgqftil4hog', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formBody.toString()
      });
      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', subject: '', message: '' });
        setTimeout(() => setSubmitStatus('idle'), 5000);
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    if (name === 'message' && value.length > 500) return;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <section id="contact" className="py-16 sm:py-24 bg-stone-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 sm:gap-16">
          {/* Left: info */}
          <div>
            <h2 className="font-serif text-4xl sm:text-5xl text-gray-900 mb-4 sm:mb-6">
              Let's Connect
            </h2>
            <p className="text-gray-600 text-base sm:text-lg leading-relaxed mb-6 sm:mb-10">
              Whether you have questions about my work, speaking engagements, or simply want to share your thoughts on '21 Days', I'd love to hear from you. Your feedback and stories inspire my continued writing journey.
            </p>

            <div className="space-y-4 mb-6 sm:mb-10">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 flex items-center justify-center">
                  <i className="ri-mail-line text-2xl text-amber-600"></i>
                </div>
                <span className="text-gray-600 text-sm sm:text-base">contact@forrestjamison.com</span>
              </div>
            </div>

            <div className="flex gap-4">
              <a href="https://instagram.com" target="_blank" rel="noopener noreferrer"
                className="w-11 h-11 flex items-center justify-center text-gray-700 hover:text-amber-600 transition-colors cursor-pointer" aria-label="Instagram">
                <i className="ri-instagram-line text-2xl"></i>
              </a>
              <a href="https://twitter.com" target="_blank" rel="noopener noreferrer"
                className="w-11 h-11 flex items-center justify-center text-gray-700 hover:text-amber-600 transition-colors cursor-pointer" aria-label="Twitter">
                <i className="ri-twitter-x-line text-2xl"></i>
              </a>
              <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer"
                className="w-11 h-11 flex items-center justify-center text-gray-700 hover:text-amber-600 transition-colors cursor-pointer" aria-label="LinkedIn">
                <i className="ri-linkedin-line text-2xl"></i>
              </a>
            </div>
          </div>

          {/* Right: form */}
          <div>
            <form onSubmit={handleSubmit} className="bg-white rounded-2xl p-5 sm:p-8" data-readdy-form>
              <input type="hidden" name="form-name" value="Contact Form" />
              <div className="space-y-4 sm:space-y-5">
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Your Name"
                  required
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-amber-600 focus:outline-none transition-colors text-sm"
                />
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Your Email"
                  required
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-amber-600 focus:outline-none transition-colors text-sm"
                />
                <input
                  type="text"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  placeholder="Subject"
                  required
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-amber-600 focus:outline-none transition-colors text-sm"
                />
                <div>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Your Message"
                    required
                    maxLength={500}
                    rows={5}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-amber-600 focus:outline-none transition-colors resize-none text-sm"
                  />
                  <div className="text-right text-xs text-gray-400 mt-1">
                    {formData.message.length}/500
                  </div>
                </div>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-gray-900 hover:bg-gray-800 text-white py-4 rounded-lg font-medium transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap text-sm sm:text-base"
                >
                  {isSubmitting ? 'Sending...' : (
                    <><span>Send Message</span><i className="ri-arrow-right-line"></i></>
                  )}
                </button>
                {submitStatus === 'success' && (
                  <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg text-sm">
                    Thank you! Your message has been sent successfully.
                  </div>
                )}
                {submitStatus === 'error' && (
                  <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-sm">
                    Sorry, there was an error. Please try again.
                  </div>
                )}
              </div>
            </form>
            <p className="text-center text-gray-500 text-xs sm:text-sm mt-4 sm:mt-6">
              Your information is secure and will never be shared. Response within 48 hours.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
